import React from 'react';
import { useSelector } from 'react-redux';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { NoSsr } from '@mui/material';
import AvailabilityCalendar from '../../molecules/Availability/AvailabilityCalendar';
import { useTranslation } from '@/src/i18n';
import {
  useMediaQueryBreakPoint,
  useFirstAvailability,
  IS_PHONE_OR_TABLET,
} from '../../../src/hooks';
import { DEFAULT_TIMEZONE } from '@/src/constants';
import { dayjsWithTimezone } from '@/src/utils';
import SearchCardFirstAvailability from '@/components/organisms/SearchCard/SearchCardFirstAvailability';
import { isDocavenueClient, isRdvEnabled } from '@/components/utils/card';

interface Props {
  tlc?: boolean;
  item: Required<CardHcd>;
  onClick: React.ReactEventHandler;
  onSuccess?: (arg0: { total: number | null }) => void;
  onLoad?: () => void;
}

const SearchCardAvailabilities: React.FC<Props> = ({
  tlc,
  item,
  onClick,
  onSuccess,
  onLoad,
}) => {
  const { t } = useTranslation();
  const isPhoneOrTablet = useMediaQueryBreakPoint(IS_PHONE_OR_TABLET);
  const dayjsTZ = dayjsWithTimezone(
    item?.settings?.timeZone ?? DEFAULT_TIMEZONE,
  );

  const suggestion = useSelector((store: any) => store.suggestions.item);
  const firstFreeTimeslot = useFirstAvailability(item, 'ALL', {
    forceFetch: true,
  });
  const firstFreeTimeslotdayjs = React.useMemo(
    () => (firstFreeTimeslot ? dayjsTZ(firstFreeTimeslot) : null),
    [firstFreeTimeslot],
  );
  const onAvailabilityCalendarClick = React.useCallback(() => {
    onClick(undefined as any);
  }, []);

  React.useEffect(() => {
    if (!firstFreeTimeslot) onSuccess?.({ total: 0 });
  }, [firstFreeTimeslot]);

  if (tlc) {
    return null;
  }

  if (!isDocavenueClient(item)) {
    return (
      <div className="no-availability not-client">
        {t('closest_availability_not_client')}
      </div>
    );
  }

  // TODO: If firstFreeTimeslot is not available, call onSuccess for stop loading???, so complicated stack in here
  /* If is client and no dispo */
  if (!isRdvEnabled(item) || !firstFreeTimeslotdayjs) {
    return (
      <div className="no-availability">
        <span className="info-availability">
          {t('closest_availability_none_short')}
        </span>
      </div>
    );
  }

  return (
    <NoSsr>
      {!isPhoneOrTablet && (
        <AvailabilityCalendar
          onSuccess={onSuccess}
          card={item}
          onLoad={onLoad}
          practitionerId={item?.professional?.practitionerId}
          centerId={item?.center?.id}
          settings={item?.settings}
          consultationType={item?.type}
          onClick={onAvailabilityCalendarClick}
          specialityId={
            suggestion.objectType === 'speciality' &&
            suggestion.shortName !== 'pharmacie'
              ? suggestion.id
              : undefined
          }
        />
      )}
      {firstFreeTimeslotdayjs && (
        <SearchCardFirstAvailability
          firstFreeTimeslotdayjs={firstFreeTimeslotdayjs}
          onClick={onClick}
        />
      )}
    </NoSsr>
  );
};

export default SearchCardAvailabilities;
